import React from "react";
import { Layout, Button, Input, Popconfirm, message } from "antd";
import { ReloadOutlined, SearchOutlined, UndoOutlined } from "@ant-design/icons";
import AppHeader from "../../AppHeader/AppHeader";
import RedactionChip from "../../common/RedactionChip";
import { anonApi } from "../../util/anonymizerApi";

const { Content } = Layout;

// render a redacted string ("call <PHONE> now") as text + redaction chips,
// so the restore list shows WHAT was hidden without exposing the original PII
const CHIP = /<([A-Z0-9_]+)>/g;
function renderRedacted(text) {
    const s = String(text === null || text === undefined ? "" : text);
    const out = [];
    let last = 0;
    let m;
    CHIP.lastIndex = 0;
    while ((m = CHIP.exec(s)) !== null) {
        if (m.index > last) out.push(s.slice(last, m.index));
        out.push(<RedactionChip key={m.index} type={m[1]} />);
        last = m.index + m[0].length;
    }
    if (last < s.length) out.push(s.slice(last));
    return out;
}

function daysLeft(expiresAt) {
    if (!expiresAt) return null;
    return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);
}

// remaining-retention pill: calm when there's time, warmer as it runs out
function RetentionPill({ expiresAt }) {
    const d = daysLeft(expiresAt);
    if (d === null) return null;
    const tone = d <= 1
        ? { c: "var(--sc-danger)", b: "var(--sc-danger-soft)" }
        : d <= 7
            ? { c: "var(--sc-brand-ink)", b: "var(--sc-brand-soft)" }
            : { c: "var(--sc-ok)", b: "var(--sc-ok-soft)" };
    const label = d <= 0 ? "expires today" : d === 1 ? "1 day left" : `${d} days left`;
    return (
        <span style={{
            fontSize: 12, fontWeight: 700, color: tone.c, background: tone.b,
            padding: "2px 10px", borderRadius: 99, whiteSpace: "nowrap"
        }}>{label}</span>
    );
}

function TriggerBadge({ entry }) {
    const auto = entry.trigger === "auto";
    return (
        <span style={{
            fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
            color: auto ? "var(--sc-ok)" : "var(--sc-brand-ink)",
            background: auto ? "var(--sc-ok-soft)" : "var(--sc-brand-soft)",
            padding: "2px 10px", borderRadius: 99
        }}>{auto ? "Automatic" : "Reviewed"}</span>
    );
}

/** One restorable record as a set-apart card: what's hidden + how long it can
 *  still be brought back + a guarded Restore action. */
const RestoreCard = ({ entry, onRestore, busy }) => {
    const fields = entry.fields || {};
    const fieldNames = Object.keys(fields);
    return (
        <div style={{
            background: "var(--sc-surface)", border: "1px solid var(--sc-border)",
            borderRadius: 16, boxShadow: "var(--sc-sh-1)", marginBottom: 12, overflow: "hidden"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "15px 20px", flexWrap: "wrap" }}>
                <b style={{ fontSize: 14.5, letterSpacing: "-.01em" }}>{entry.ticket || "—"}</b>
                <span style={{ color: "var(--sc-ink-3)", fontSize: 13 }}>{entry.form} · {entry.recordId}</span>
                <TriggerBadge entry={entry} />
                <span style={{ marginLeft: "auto" }} />
                <span style={{ color: "var(--sc-ink-3)", fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                    {new Date(entry.at).toLocaleString()}
                </span>
                <RetentionPill expiresAt={entry.expiresAt} />
                <Popconfirm
                    title="Restore the original values?"
                    description={`This writes the original personal data back into ${entry.recordId}.`}
                    okText="Restore" okButtonProps={{ danger: true }}
                    onConfirm={() => onRestore(entry)}
                >
                    <Button size="small" danger icon={<UndoOutlined />} loading={busy}>Restore</Button>
                </Popconfirm>
            </div>
            <div style={{ borderTop: "1px solid var(--sc-border)", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                {fieldNames.map((f) => (
                    <div key={f} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--sc-ink-3)" }}>{f}</span>
                        <div style={{
                            fontSize: 13.5, lineHeight: 1.7, color: "var(--sc-ink)",
                            background: "var(--sc-inset)", border: "1px solid var(--sc-border)",
                            borderRadius: 9, padding: "8px 11px", wordBreak: "break-word"
                        }}>
                            {renderRedacted(fields[f] && fields[f].new)}
                        </div>
                    </div>
                ))}
                <span style={{ fontSize: 12, color: "var(--sc-ink-3)" }}>
                    The original is kept encrypted and can be restored above until it expires.
                </span>
            </div>
        </div>
    );
};

/**
 * Restore data: a dedicated, browsable view of every record whose personal
 * data was anonymized (automatically OR after review) and can still be brought
 * back within the retention window. Restore writes the sealed original back.
 */
const Restore = () => {
    const [entries, setEntries] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [busyId, setBusyId] = React.useState(null);
    const [query, setQuery] = React.useState("");

    const load = React.useCallback(() => {
        setLoading(true);
        anonApi("/v1/audit", { method: "GET" })
            .then((r) => setEntries((Array.isArray(r) ? r : []).filter((e) => e.restorable)))
            .catch((e) => message.error("Could not load restorable records: " + e.message))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const restore = (entry) => {
        setBusyId(entry.id);
        anonApi("/v1/restore", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ id: entry.id })
        })
            .then((r) => { message.success(`${entry.recordId}: ${r.restored.length} field(s) restored`); load(); })
            .catch((e) => message.error("Restore failed: " + e.message))
            .finally(() => setBusyId(null));
    };

    const q = query.trim().toLowerCase();
    const shown = q
        ? entries.filter((e) => (e.recordId || "").toLowerCase().includes(q) || (e.ticket || "").toLowerCase().includes(q))
        : entries;
    const expiringSoon = entries.filter((e) => { const d = daysLeft(e.expiresAt); return d !== null && d <= 7; }).length;

    return (
        <Layout style={{ minHeight: "100vh", background: "var(--sc-ground)" }}>
            <AppHeader area="restore" />
            <Content style={{ padding: 0 }}>
                <div className="sc-page">
                    <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 680, letterSpacing: "-.01em" }}>Restore data</h2>
                        <span style={{
                            fontSize: 12.5, fontWeight: 700, color: "var(--sc-ink-2)",
                            background: "var(--sc-inset)", padding: "2px 10px", borderRadius: 99
                        }}>{entries.length}</span>
                        {expiringSoon > 0 && (
                            <span style={{
                                fontSize: 12.5, fontWeight: 700, color: "var(--sc-brand-ink)",
                                background: "var(--sc-brand-soft)", padding: "2px 10px", borderRadius: 99
                            }}>{expiringSoon} expiring soon</span>
                        )}
                        <span style={{ color: "var(--sc-ink-2)", fontSize: 14 }}>
                            — anonymized records whose original is still recoverable.
                        </span>
                        <span style={{ marginLeft: "auto" }} />
                        <Input
                            allowClear
                            prefix={<SearchOutlined style={{ color: "var(--sc-ink-3)" }} />}
                            placeholder="Filter by ticket or record id"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ width: 260 }}
                        />
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
                    </div>

                    {shown.length === 0 ? (
                        <div style={{
                            background: "var(--sc-surface)", border: "1px dashed var(--sc-border-strong)", borderRadius: 16,
                            padding: "40px 20px", textAlign: "center", color: "var(--sc-ink-2)"
                        }}>
                            {entries.length === 0
                                ? "Nothing to restore — no anonymized records are within the retention window."
                                : "No records match your filter."}
                        </div>
                    ) : (
                        shown.map((entry) => (
                            <RestoreCard key={entry.id} entry={entry} onRestore={restore} busy={busyId === entry.id} />
                        ))
                    )}

                    <p style={{ fontSize: 12, color: "var(--sc-ink-3)", marginTop: 16, maxWidth: "70ch" }}>
                        Originals are stored encrypted and are automatically purged after the retention period.
                        Once purged, a record disappears from this list and can no longer be restored.
                    </p>
                </div>
            </Content>
        </Layout>
    );
};

export default Restore;
